import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/layout/PageLayout';
import useConversationMessages from '../hooks/useConversationMessages';
import MessageList from '../components/messaging/MessageList';
import MessageInput from '../components/messaging/MessageInput';
import messagingService from '../services/messagingService';

function ConversationPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const {
    messages,
    loading,
    sending,
    error,
    hasMore,
    sendMessage,
    loadMore,
    updateMessage,
    removeMessage,
  } = useConversationMessages(conversationId, { markReadOnMount: true });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/?auth=login');
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) {
    return null;
  }

  const handleSend = (content) => {
    sendMessage(content, 'text');
  };

  const handleDeleteChat = async () => {
    if (!conversationId || !window.confirm('Delete this entire chat? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await messagingService.deleteConversation(conversationId);
      navigate('/messages');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete chat';
      window.alert(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <PageLayout title="Conversation - PakPoints">
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow flex flex-col min-h-[500px]">
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/messages')}
              className="text-gray-600 hover:text-gray-900"
              aria-label="Back to messages"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-lg font-semibold text-gray-900">Conversation</h1>
          </div>
          <button
            type="button"
            onClick={handleDeleteChat}
            disabled={deleting}
            className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
            aria-label="Delete chat"
          >
            {deleting ? 'Deleting…' : 'Delete chat'}
          </button>
        </div>
        {error && (
          <div className="px-4 py-2 bg-red-50 text-red-700 text-sm">
            {error}
          </div>
        )}
        <div className="flex flex-col flex-1 min-h-0">
          <MessageList
            messages={messages}
            currentUserId={user?.id}
            loading={loading}
            onLoadMore={loadMore}
            hasMore={hasMore}
            onEditMessage={updateMessage}
            onDeleteMessage={removeMessage}
          />
          <MessageInput onSend={handleSend} disabled={sending} placeholder="Type a message..." />
        </div>
      </div>
    </PageLayout>
  );
}

export default ConversationPage;
